/**
 * Chip — filter/tag chip with optional remove button and toggle state.
 *
 * @see docs/milestones/M0.6-UI-PRIMITIVES.md §18
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { View, Text } from 'react-native'
import { X } from '@/components/ui/icons'
import { selection } from '@/lib/utils/haptics'
import { Pressable } from './Pressable'
import { tintIconNode } from './tint-icon-node'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, RADIUS, SPACING, withAlpha, INTERACTION } from '../../lib/theme/tokens'
import { FONTS } from '../../lib/brand'
import { cn } from '../../lib/theme/variants'

// ─── Types ──────────────────────────────────────────────────────────────────

type ChipVariant = 'filled' | 'outlined' | 'ghost'
type ChipColor = 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'
type ChipSize = 'sm' | 'md'

interface ChipProps {
  label: string
  variant?: ChipVariant
  color?: ChipColor
  icon?: React.ReactNode
  removable?: boolean
  onRemove?: () => void
  onPress?: () => void
  selected?: boolean
  disabled?: boolean
  size?: ChipSize
  className?: string
}

// ─── Size Map ───────────────────────────────────────────────────────────────

const SIZE_MAP = {
  sm: { height: 24, paddingH: SPACING.sm, fontSize: 12, iconSize: 12 },
  md: { height: 32, paddingH: SPACING.sm + SPACING.xs, fontSize: 13, iconSize: 14 },
} as const

// ─── Helpers ────────────────────────────────────────────────────────────────

function resolveColor(chipColor: ChipColor, colors: ReturnType<typeof useSemanticColors>): string {
  switch (chipColor) {
    case 'primary':
      return colors.primary
    case 'success':
      return colors.success
    case 'warning':
      return colors.warning
    case 'error':
      return colors.error
    case 'info':
      return colors.info
    default:
      return colors.textSecondary
  }
}

// ─── Chip ───────────────────────────────────────────────────────────────────

export function Chip({
  label,
  variant = 'filled',
  color = 'default',
  icon,
  removable = false,
  onRemove,
  onPress,
  selected = false,
  disabled = false,
  size = 'md',
  className,
}: ChipProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const tokens = SIZE_MAP[size]
  const chipColor = resolveColor(color, colors)

  const bg =
    variant === 'filled' ? (selected ? chipColor : withAlpha(chipColor, 0.09)) : 'transparent'

  const textColor = variant === 'filled' && selected ? '#FFFFFF' : chipColor
  const resolvedIcon = icon ? tintIconNode(icon, textColor) : null

  const borderColor = variant === 'outlined' ? chipColor : 'transparent'

  const handlePress = () => {
    if (disabled) return
    void selection()
    onPress?.()
  }

  const handleRemove = () => {
    if (disabled) return
    void selection()
    onRemove?.()
  }

  const content = (
    <View
      style={{
        height: tokens.height,
        paddingHorizontal: tokens.paddingH,
        borderRadius: RADIUS.full,
        backgroundColor: bg,
        borderWidth: variant === 'outlined' ? 1 : 0,
        borderColor,
        flexDirection: 'row',
        alignItems: 'center',
        opacity: disabled ? 0.4 : 1,
      }}
      className={cn(className)}
    >
      {resolvedIcon ? <View style={{ marginRight: SPACING.xs }}>{resolvedIcon}</View> : null}
      <Text
        style={{
          fontSize: tokens.fontSize,
          fontFamily: FONTS.body,
          color: textColor,
        }}
        selectable={false}
      >
        {label}
      </Text>
      {removable && (
        <Pressable
          onPress={handleRemove}
          hitSlop={6}
          style={{ marginLeft: SPACING.xs }}
          accessibilityRole="button"
          accessibilityLabel={t('a11y.remove_chip')}
        >
          <X size={tokens.iconSize} color={textColor} strokeWidth={2} />
        </Pressable>
      )}
    </View>
  )

  if (onPress) {
    return (
      <Pressable
        onPress={handlePress}
        disabled={disabled}
        hitSlop={size === 'sm' ? 10 : 6}
        accessibilityRole="button"
        accessibilityState={{ selected }}
        // The pill stays 24 or 32 tall; the touchable box around it is 44.
        // hitSlop alone was doing this job, and react-native-web ignores
        // hitSlop — so on web every selectable chip, including the whole
        // amenities picker, was a 32px target.
        style={{ minHeight: INTERACTION.minTouchTarget, justifyContent: 'center' }}
      >
        {content}
      </Pressable>
    )
  }

  return content
}
